import type { Card, Language, StudyBlock } from '../../../shared/types';
import type { Speech } from '../hooks/useSpeech';

interface Props {
  blocks: StudyBlock[];
  /** The section's deck, so vocabulary tables can resolve their card ids. */
  cards: Card[];
  language: Language;
  speech: Speech;
  speechRate: number;
}

function SpeakerIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M11 5 6 9H3v6h3l5 4V5Z" fill="currentColor" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M15.5 8.5a4.5 4.5 0 0 1 0 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Renders the study tab.
 *
 * The content is a list of typed blocks rather than prose with markup, which is
 * what lets a vocabulary table be a real table of the section's own cards — the
 * words being read here are the same records that get drilled next door, and a
 * mismatch is a build failure rather than a rendering quirk.
 */
export default function StudyBlocks({ blocks, cards, language, speech, speechRate }: Props) {
  const byId = new Map(cards.map((card) => [card.id, card]));

  return (
    <div className="study">
      {blocks.map((block, index) => {
        switch (block.kind) {
          case 'prose':
            return (
              <p key={index} className="study-prose">
                {block.text}
              </p>
            );

          case 'rule':
            return (
              <div key={index} className="study-rule">
                <h3 className="study-rule-title">{block.title}</h3>
                <p>{block.text}</p>
              </div>
            );

          case 'note':
            return (
              <aside key={index} className="study-note">
                <span className="study-note-mark" aria-hidden="true">
                  ※
                </span>
                <p>{block.text}</p>
              </aside>
            );

          case 'vocab': {
            const entries = block.cardIds
              .map((id) => byId.get(id))
              .filter((card): card is Card => Boolean(card));
            return (
              <section key={index} className="study-vocab">
                {block.title && <h3 className="study-vocab-title">{block.title}</h3>}
                {block.intro && <p className="study-vocab-intro">{block.intro}</p>}
                <ul className="vocab-list">
                  {entries.map((card) => (
                    <li key={card.id} className="vocab-entry">
                      <span className={`vocab-target vocab-target--${language.script}`} lang={language.code}>
                        {card.target}
                      </span>
                      {card.translit && <span className="vocab-translit">{card.translit}</span>}
                      <span className="vocab-translation">{card.translation}</span>
                      {speech.voiceAvailable && (
                        <button
                          type="button"
                          className="vocab-speak"
                          onClick={() => speech.speak(card.target, speechRate)}
                          aria-label={`Play ${card.target}`}
                        >
                          <SpeakerIcon />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );
          }

          case 'examples':
            return (
              <section key={index} className="study-examples">
                {block.title && <h3 className="study-vocab-title">{block.title}</h3>}
                {block.intro && <p className="study-vocab-intro">{block.intro}</p>}
                <ul className="example-list">
                  {block.rows.map((row, rowIndex) => (
                    <li key={rowIndex} className="example-row">
                      <span className="example-target" lang={language.code}>
                        {row.target}
                      </span>
                      {row.translit && <span className="example-translit">{row.translit}</span>}
                      <span className="example-translation">{row.translation}</span>
                      {speech.voiceAvailable && (
                        <button
                          type="button"
                          className="vocab-speak"
                          onClick={() => speech.speak(row.target, speechRate)}
                          aria-label={`Play ${row.target}`}
                        >
                          <SpeakerIcon />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );

          case 'table':
            return (
              <section key={index} className="study-table-wrap">
                {block.title && <h3 className="study-vocab-title">{block.title}</h3>}
                {block.intro && <p className="study-vocab-intro">{block.intro}</p>}
                <div className="table-scroll">
                  <table className="study-table">
                    <thead>
                      <tr>
                        {block.columns.map((column, columnIndex) => (
                          <th key={columnIndex} scope="col">
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {block.rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex} lang={language.code}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            );

          default:
            // A bundle from a newer build than this client. Skipping the block
            // keeps the rest of the page readable.
            return null;
        }
      })}
    </div>
  );
}
